import { ref } from 'vue'

// Singleton: the router guard lives outside any component instance, so it
// needs a channel into RecordView (which owns the actual recording and
// renders the discard-confirmation modal) that survives independently of
// any one RecordView mount.
const isRecordingActive = ref(false)
const confirmationRequested = ref(false)

let resolveConfirmation: ((allow: boolean) => void) | null = null
// A second confirmLeave() call can arrive before the first is answered — a
// rapid double back-tap fires two overlapping navigation attempts. Sharing
// one pending promise means both callers get the same answer instead of the
// second call's resolver silently orphaning the first's, which would leave
// that first caller's navigation guard awaiting a promise that never settles.
let pendingConfirmation: Promise<boolean> | null = null

export function useRecordingGuard() {
  function setActive(active: boolean): void {
    isRecordingActive.value = active
  }

  // Called from the router's beforeEach when navigation would leave the
  // Record screen mid-recording. Resolves once RecordView's confirmation
  // modal is answered — true lets the navigation (native or in-app) proceed,
  // false cancels it (vue-router restores the prior history entry for a
  // popstate-triggered navigation).
  function confirmLeave(): Promise<boolean> {
    if (!isRecordingActive.value) return Promise.resolve(true)
    if (pendingConfirmation) return pendingConfirmation
    confirmationRequested.value = true
    pendingConfirmation = new Promise((resolve) => {
      resolveConfirmation = resolve
    })
    return pendingConfirmation
  }

  function answerConfirmation(allow: boolean): void {
    confirmationRequested.value = false
    resolveConfirmation?.(allow)
    resolveConfirmation = null
    pendingConfirmation = null
  }

  return { isRecordingActive, confirmationRequested, setActive, confirmLeave, answerConfirmation }
}
